import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DisclaimerNote from "@/components/DisclaimerNote";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { setErr("Missing reset token. Request a new reset link."); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 grid-noise pointer-events-none" />
      <div className="w-full max-w-md hard-card hard-card-shadow p-8 relative">
        <Link to="/login" className="overline mb-8 inline-block hover:underline">← BACK TO SIGN IN</Link>
        <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Choose New Password</h1>
        <p className="text-sm text-muted-foreground mb-6">Pick a strong password of at least 8 characters. You'll use it the next time you sign in.</p>

        {!token && (
          <div data-testid="reset-password-missing-token" className="border-2 border-bad text-bad font-mono text-xs p-3 mb-4">
            No reset token found in URL. Request a new link from the <Link to="/forgot-password" className="underline">forgot password page</Link>.
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" autoComplete="off">
          <div>
            <div className="overline mb-2">New Password (min 8 chars)</div>
            <input
              data-testid="reset-password-new"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full px-4 py-3 border-2 border-ink font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink"
            />
          </div>
          <div>
            <div className="overline mb-2">Confirm Password</div>
            <input
              data-testid="reset-password-confirm"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full px-4 py-3 border-2 border-ink font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink"
            />
          </div>
          {err && <div data-testid="reset-password-error" className="border-2 border-bad text-bad font-mono text-xs p-3">{err}</div>}
          <button
            data-testid="reset-password-submit"
            type="submit"
            disabled={busy || !token}
            className="hard-btn w-full py-3 font-mono uppercase tracking-widest text-sm disabled:opacity-60"
          >
            {busy ? "Updating…" : "Update Password →"}
          </button>
        </form>
      <DisclaimerNote />
      </div>
    </div>
  );
}
